import RegularText from '@/components/RegularText';
import { getOptionsMultipleCode, getOptionsSelectedAdd, getOptionsSelectedRemove } from '@/helpers';
import { stringify, uniq } from '@/helpers/string';
import { IAutoComplete, IOptions } from '@/types';
import { Autocomplete, Badge, LegacyStack, Tag } from '@shopify/polaris';
import { OptionDescriptor, SectionDescriptor } from '@shopify/polaris/build/ts/src/types';
import { memo, useEffect, useState } from 'react';

function AutoCompleteSectionWithTag({ ...props }: IAutoComplete.IPropsSection) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<SectionDescriptor[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setOptions(props.deselectedOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(props.deselectedOptions)]);

  useEffect(() => {
    if (!props.allowMultiple) {
      setInputValue(props.tag[0]?.label || '');
    }
  }, [props.allowMultiple, props.tag]);

  useEffect(() => {
    if (!props.requiredIndicator || props.selectedOptions.length > 0) {
      setError('');
    }
  }, [props.requiredIndicator, props.selectedOptions.length]);

  const updateText = (value: string) => {
    if (error) {
      setError('');
    }
    if (props.handleSearch) {
      setInputValue(value);
      props.handleSearch(value);
    } else {
      setInputValue(value);
      const resultOptions = props.deselectedOptions.map((option) => {
        return {
          ...option,
          options: option.options?.filter((item) => item.label.toLocaleLowerCase().includes(value.toLowerCase())),
        };
      });
      setOptions(resultOptions);
    }
  };

  const handleReturnValue = (selected: string[]) => {
    let listOptions: Array<IOptions> = [];
    props.deselectedOptions.forEach((item) => {
      listOptions = listOptions.concat(item.options);
    });

    const isAdd = selected.length > props.selectedOptions.length;
    let arrayMultipleCode = selected
      ?.filter((item) => item.includes(','))
      .join(',')
      .split(',');
    if (arrayMultipleCode.length === 1 && arrayMultipleCode[0] === '') {
      arrayMultipleCode = [];
    }
    let handleSelected = [...selected];
    if (isAdd) {
      handleSelected = uniq(handleSelected.concat(arrayMultipleCode));

      const multipleCodeExists = getOptionsMultipleCode(handleSelected, listOptions);

      multipleCodeExists.forEach((item) => {
        handleSelected.push(item.value);
      });
    } else {
      const codeRemoved = props.tag?.filter((tag) => !selected.some((item) => item === tag.value));
      if (codeRemoved[0]?.value?.includes(',')) {
        handleSelected = handleSelected?.filter((item) => !codeRemoved[0]?.value?.split(',').includes(item));
      } else {
        handleSelected = handleSelected?.filter((item) => !item.includes(codeRemoved[0]?.value));
      }
    }

    const result = !props.allowMultiple
      ? [listOptions.find((item) => item.value === selected[0]) || { value: '', label: '' }]
      : isAdd
      ? getOptionsSelectedAdd(handleSelected, props.tag, listOptions, 'value')
      : getOptionsSelectedRemove(handleSelected, props.tag);

    return result;
  };

  const removeTag = (tag: string) => () => {
    const newTags = props.selectedOptions?.filter((item) => item !== tag);
    props.setSelectedOptions(handleReturnValue(newTags));
    if (newTags.length === 0) {
      props?.customeHandleError?.('Please select at least 1 country');
    }
  };

  const handleOnSelect = (value: Array<string>) => {
    props.setSelectedOptions(handleReturnValue(value));
    if (error) {
      setError('');
    }
  };

  const textField = (
    <Autocomplete.TextField
      error={props.customErrorMessage ? props.customErrorMessage : props.requiredIndicator ? error : undefined}
      onChange={updateText}
      label={props.label}
      value={inputValue}
      autoComplete="off"
      requiredIndicator={props.requiredIndicator}
      placeholder={props.placeholder}
      disabled={props.disable}
      helpText={props.helpText}
      onBlur={
        props.customeHandleError
          ? () => {
              if (props.selectedOptions.length === 0) {
                props?.customeHandleError?.('Please select at least 1 country');
              } else {
                props?.customeHandleError?.('');
              }
            }
          : props.requiredIndicator
          ? () => {
              if (props.selectedOptions.length === 0) {
                setError('This field is required');
              }
            }
          : undefined
      }
    />
  );

  const tagsMarkup =
    props.tag.length > 0
      ? props.tag.map((tag) => {
          return (
            <Tag key={`option${tag.value}`} onRemove={removeTag(tag.value)}>
              {tag.label || ''}
            </Tag>
          );
        })
      : null;

  const selectedTagMarkup =
    props.selectedOptions.length > 0 && props.allowMultiple ? <LegacyStack spacing="extraTight">{tagsMarkup}</LegacyStack> : null;

  const handleGenerateOptions = () => {
    return options?.map((item) => {
      return {
        ...item,
        options: props.badge
          ? item.options?.map((option) => {
              return {
                ...option,
                label: (
                  <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <RegularText>{option.label}</RegularText>
                    {/*@ts-ignore:next-line */}
                    {option?.badge ? (
                      /*@ts-ignore:next-line */
                      option?.badgeContent ? (
                        /*@ts-ignore:next-line */
                        <Badge tone="attention">{option?.badgeContent}</Badge>
                      ) : (
                        props.badge
                      )
                    ) : null}
                  </div>
                ),
              };
            })
          : item.options,
      };
    }) as SectionDescriptor[] | OptionDescriptor[];
  };

  return (
    <div>
      <Autocomplete
        allowMultiple={props.allowMultiple}
        options={handleGenerateOptions()}
        selected={props.selectedOptions}
        textField={textField}
        onSelect={handleOnSelect}
      />
      <div style={{ marginTop: '8px' }}>{selectedTagMarkup}</div>
    </div>
  );
}

export default memo(AutoCompleteSectionWithTag);
